/**
 * Francis, dans la démo — le conseil à droite, et ses réponses aux boutons.
 *
 * Kayla, 07-10-2026 : « Francis, il conseille rien, il n'y a rien. Il n'y a
 * pas le vrai comportement de Francis ». Au poste, le conseiller
 * (`cyrano/conseil/conseiller.py`) lit le fil et rend l'ÉTAPE de la vente, la
 * stratégie, et des gestes prêts — un message à insérer, une vente préparée.
 * La démo n'a pas de modèle : elle LIT LA MÊME SITUATION par règles (ce qu'il
 * vient de dire, s'il a reçu son flow, s'il a payé, si un lien attend) et rend
 * la même forme. Ce qu'elle dit est la méthode de Kayla (14-09-2026), mot
 * pour mot quand elle existe :
 *
 *   · ELLE propose, jamais « ton budget ? » ;
 *   · première vidéo à 50, jamais moins ; puis 70 · 100 · 150 · 200 ;
 *   · la vidéo se vend comme faite pour lui — durée, ce qu'on y voit ;
 *   · un prix ne redescend pas : 50 → 40 au plus ;
 *   · le réel se détourne : ni oui, ni date, ni prix.
 *
 * ⛔ Toutes les phrases proposées ici existent dans `langues.ts` : c'est ce
 * qui permet à un fan étranger de les recevoir traduites.
 */
import type { ConseilDuChatteur, SuggestionDuConseiller } from "@/panels/fan/conseil/api";
import { ETAPES_LISIBLES } from "@/panels/fan/conseil/api";
import type { SuggestionDeScript } from "@/panels/fan/suggestionScript/api";

import { leFlowASuggerer, leRunActif } from "./flows";
import {
  MOTS_DE_L_OBJECTION,
  MOTS_DU_GRATUIT,
  MOTS_DU_PRIX,
  MOTS_DU_REEL,
} from "./langues";
import type { ConversationDemo } from "./monde";

/* ────────────────────────────────────────────────────────────────────────── */
/* Les phrases                                                                */
/* ────────────────────────────────────────────────────────────────────────── */

const BIENVENUE = "hey toi 😊 bienvenue, t'as fait vite";
const LA_VIDEO_A_50 =
  "j'ai une vidéo de 6 min où je me touche en pensant à un mec comme toi… rien que pour toi c'est 50 😏";
const LE_PRIX_TIENT = "c'est 50 bb, et crois moi tu vas pas le regretter 😏";
const A_40_PAS_MOINS = "si t'as pas tout, je peux te la faire à 40, mais pas moins";
const LE_REEL = "en vrai c'est pas possible, mais je peux te montrer ce que je ferais si t'étais là 😏";
const MERCI = "merci bb 😘 regarde la jusqu'au bout et dis moi ce que t'en penses";
const LA_MARCHE_SUIVANTE = "j'ai encore plus fou que ça, tu veux que je te montre ? 😏";
const REVENIR = "t'es passé où ? j'ai pensé à toi aujourd'hui 😘";
const LE_LIEN = "t'as vu le lien ? je l'ai fait rien que pour toi 😘";
const CHAUFFER = "dis moi ce que t'aimerais voir, je vais te chauffer comme il faut 😏";

/** L'échelle de Kayla. La marche suivante dépend de ce qu'il a déjà payé. */
const ECHELLE = [50, 70, 100, 150, 200];

/** La marche suivante : on monte d'un cran par vidéo déjà achetée (cumul
 *  50 → 70 → 100 → 150 → 200), et on ne redescend jamais. */
function laMarche(conv: ConversationDemo): number {
  let cumul = 0;
  for (const prix of ECHELLE) {
    cumul += prix;
    if (conv.fiche.revenue_cents / 100 < cumul) return prix;
  }
  return ECHELLE[ECHELLE.length - 1]!;
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Lire la situation                                                          */
/* ────────────────────────────────────────────────────────────────────────── */

const LANGUES: Record<string, string> = {
  en: "anglais", de: "allemand", it: "italien", es: "espagnol", pt: "portugais",
};

function dernierEntrant(conv: ConversationDemo) {
  for (let i = conv.messages.length - 1; i >= 0; i--) {
    const m = conv.messages[i]!;
    if (m.direction === "in") return m;
  }
  return null;
}

/**
 * Ce que le fan a écrit DEPUIS notre dernier message, en français — c'est ce
 * qu'on juge. Pas seulement sa dernière bulle : « on se voit quand en vrai ? »
 * suivi de « je peux monter samedi » parle toujours de se voir.
 */
function cequIlDit(conv: ConversationDemo): string {
  const siens: string[] = [];
  for (let i = conv.messages.length - 1; i >= 0; i--) {
    const m = conv.messages[i]!;
    if (m.direction !== "in") break;
    siens.unshift(m.traductions?.fr ?? m.content ?? "");
  }
  return siens.join(" ").toLowerCase();
}

function ilYaMinutes(iso: string | null | undefined): number {
  return iso ? Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60_000)) : 0;
}

function duree(min: number): string {
  if (min < 60) return `${min} min`;
  if (min < 60 * 24) return `${Math.round(min / 60)} h`;
  return `${Math.round(min / (60 * 24))} j`;
}

function resumeDe(conv: ConversationDemo): string {
  const f = conv.fiche;
  const morceaux: string[] = [];
  morceaux.push(
    f.stage === "new"
      ? `${f.fan_name} vient d'arriver (il y a ${duree(ilYaMinutes(f.created_at))}), par Twitter.`
      : `${f.fan_name} est là depuis ${duree(ilYaMinutes(f.created_at))}.`,
  );
  morceaux.push(
    f.revenue_cents > 0
      ? `Il a déjà dépensé ${Math.round(f.revenue_cents / 100)} €.`
      : "Il n'a encore rien acheté.",
  );
  morceaux.push(
    conv.flowsRecus.length > 0
      ? `Flows reçus : ${conv.flowsRecus.map((n) => `« ${n} »`).join(", ")}.`
      : "Il n'a reçu aucun flow.",
  );
  if (conv.langue !== "fr") {
    morceaux.push(
      `Il écrit en ${LANGUES[conv.langue] ?? conv.langue} : ses messages sont traduits, et ce que tu tapes part dans sa langue.`,
    );
  }
  return morceaux.join(" ");
}

interface Lecture {
  etape: string;
  strategie: string;
  messages: string[];
  /** Le prix d'une vente à préparer, quand c'est le moment. */
  vente: number | null;
  /** Ce qu'il y a à FAIRE, quand ce n'est pas un message. */
  tache: { titre: string; texte: string } | null;
}

/**
 * La situation, de la plus urgente à la plus banale. La première qui
 * s'applique gagne — c'est l'ordre dans lequel un bon chatteur regarde.
 */
export function lireLaSituation(conv: ConversationDemo): Lecture {
  const dit = cequIlDit(conv);
  const dernier = conv.messages.at(-1);
  const ilAttend = dernier?.direction === "in";

  const run = leRunActif(conv.fiche.id);
  if (run) {
    return {
      etape: "chauffe",
      strategie: `Le flow « ${run.flow_name} » écrit pour toi. Laisse-le finir : il chauffe à ta place. Ne parle pas de prix avant la fin — il se termine sur « tu vas devoir me payer », et c'est là que tu reprends.`,
      messages: [],
      vente: null,
      tache: null,
    };
  }

  if (conv.fiche.has_active_order) {
    return {
      etape: "lien",
      strategie:
        "Le lien est parti. Pas de nouvelle offre, pas de baisse : rassure-le, rappelle que c'est fait pour lui, et laisse-le payer.",
      messages: ilAttend ? [LE_LIEN] : [],
      vente: null,
      tache: null,
    };
  }

  if (conv.payeLe && ilYaMinutes(conv.payeLe) < 60 * 6) {
    const marche = laMarche(conv);
    return {
      etape: "deuxieme_marche",
      strategie: `Il vient de payer. Remercie-le, laisse-le regarder, et quand il revient chaud propose la marche suivante : ${marche} €. Un prix ne redescend jamais.`,
      messages: [MERCI, LA_MARCHE_SUIVANTE],
      vente: null,
      tache: null,
    };
  }

  if (!ilAttend) {
    const silence = ilYaMinutes(dernier?.created_at);
    if (silence > 60 * 20) {
      return {
        etape: "revenir",
        strategie:
          "Il ne répond plus depuis un moment. Reviens vers lui avec une phrase qui parle de LUI — pas de prix, pas de lien.",
        messages: [REVENIR],
        vente: null,
        tache: null,
      };
    }
    return {
      etape: "chauffe",
      strategie: "Tu as répondu : c'est à lui. Attends sa réponse avant d'écrire autre chose.",
      messages: [],
      vente: null,
      tache: null,
    };
  }

  if (MOTS_DU_REEL.test(dit)) {
    return {
      etape: "reel",
      strategie:
        "Il veut du réel — se voir, un appel, une cam. Ça ne se vend pas et ça ne se promet pas : ni oui, ni date, ni prix. Garde le rêve ouvert en une phrase, puis ramène-le à une vidéo qui met en scène ce qu'il voulait.",
      messages: [LE_REEL],
      vente: laMarche(conv),
      tache: null,
    };
  }

  if (MOTS_DE_L_OBJECTION.test(dit)) {
    return {
      etape: "hesite",
      strategie:
        "Il trouve ça cher. Un prix ne redescend pas : 50 → 40 au plus s'il ne peut vraiment pas, et jamais la même vidéo à 10. Redonne-lui envie de CETTE vidéo plutôt que de baisser.",
      messages: [LE_PRIX_TIENT, A_40_PAS_MOINS],
      vente: 40,
      tache: null,
    };
  }

  if (MOTS_DU_GRATUIT.test(dit)) {
    return {
      etape: "chauffe",
      strategie:
        "Il réclame du gratuit. Le contenu ne se donne pas : demande-lui ce qu'il aimerait voir, chauffe-le, puis propose.",
      messages: [CHAUFFER],
      vente: null,
      tache: null,
    };
  }

  if (MOTS_DU_PRIX.test(dit)) {
    const marche = laMarche(conv);
    return {
      etape: "prix",
      strategie: `Il demande le prix : c'est TOI qui proposes, jamais « c'est quoi ton budget ? ». Une vidéo faite pour lui — sa durée, ce qu'on y voit, « rien que pour toi » — à ${marche} €${marche === 50 ? " (la première vidéo, jamais moins)" : ""}. Puis le lien.`,
      messages: marche === 50 ? [LA_VIDEO_A_50] : [LA_MARCHE_SUIVANTE],
      vente: marche,
      tache: null,
    };
  }

  if (conv.flowsRecus.length === 0) {
    return {
      etape: "chauffe",
      strategie:
        "Il vient d'arriver et n'a reçu aucun flow. Ajoute-le à ta file et lance « script 1 » : le flow le chauffe à ta place et se termine sur le prix.",
      messages: [BIENVENUE],
      vente: null,
      tache: {
        titre: "Lance le flow « script 1 »",
        texte: "Flow ▾ › script 1 — ou le bouton du flow au-dessus du composeur.",
      },
    };
  }

  if (conv.fiche.moment_a_reprendre === "flow_termine") {
    return {
      etape: "apres_flow",
      strategie:
        "Le flow est fini sur « tu vas devoir me payer ». C'est à toi : propose ta vidéo maintenant, à 50.",
      messages: [LA_VIDEO_A_50],
      vente: 50,
      tache: null,
    };
  }

  return {
    etape: "chauffe",
    strategie:
      "Il parle : chauffe-le. Demande-lui ce qu'il aimerait voir, rebondis sur ce qu'il dit, puis propose une vidéo qui colle.",
    messages: [CHAUFFER],
    vente: null,
    tache: null,
  };
}

/* ────────────────────────────────────────────────────────────────────────── */
/* La vidéo à vendre — un vrai média de la créatrice                          */
/* ────────────────────────────────────────────────────────────────────────── */

interface VideoDuVault {
  id: string;
  description: string;
  duree_s: number | null;
}

const videos = new Map<string, Promise<VideoDuVault | null>>();

/**
 * Une vidéo du VRAI Vault de la créatrice — « Préparer la vente » ouvre alors
 * la Transaction avec l'œuvre à livrer, comme au poste. Une lecture : la
 * route ne porte aucun fan. Une panne rend `null`, et la vente n'est pas
 * proposée (le message, lui, reste).
 */
function uneVideoDe(creatorId: string): Promise<VideoDuVault | null> {
  let p = videos.get(creatorId);
  if (!p) {
    p = import("@/core/api/client")
      .then(({ api }) =>
        api.get<{ items: Array<{ id: string; title?: string; custom_name?: string | null; description?: string | null; duration_s: number | null; price_cents: number }> }>(
          "/api/vault",
          { params: { creator_id: creatorId, media_type: "video", limit: 60 } },
        ),
      )
      .then((r) => {
        const items = r.items ?? [];
        const v = items.find((i) => i.price_cents > 0) ?? items[0];
        if (!v) return null;
        return {
          id: v.id,
          description: v.custom_name || v.description || v.title || "Vidéo",
          duree_s: v.duration_s,
        };
      })
      .catch(() => null);
    videos.set(creatorId, p);
  }
  return p;
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Les routes                                                                 */
/* ────────────────────────────────────────────────────────────────────────── */

/** `GET /api/conversations/{id}/conseil` */
export async function leConseil(conv: ConversationDemo): Promise<ConseilDuChatteur> {
  const l = lireLaSituation(conv);
  const suggestions: SuggestionDuConseiller[] = l.messages.map((texte) => ({
    type: "message",
    titre: "Message",
    texte,
  }));
  if (l.vente !== null) {
    const video = await uneVideoDe(conv.fiche.creator_id);
    if (video) {
      const min = video.duree_s ? ` · ${Math.max(1, Math.round(video.duree_s / 60))} min` : "";
      suggestions.push({
        type: "ppv",
        titre: `Préparer la vente · ${l.vente} €`,
        texte: `${video.description}${min}`,
        content_item_id: video.id,
        prix_eur: l.vente,
      });
    }
  }
  if (l.tache) suggestions.push({ type: "tache", titre: l.tache.titre, texte: l.tache.texte });
  return {
    resume: "",
    etape: l.etape,
    strategie: l.strategie,
    suggestions,
    message_id: dernierEntrant(conv)?.id ?? null,
    raison: null,
  };
}

/** `GET /api/conversations/{id}/conseil/resume` — le résumé, au clic. */
export function leResume(conv: ConversationDemo): { resume: string; raison: null } {
  return { resume: resumeDe(conv), raison: null };
}

/**
 * `GET /api/conversations/{id}/suggestion-script` — le flow à lancer sur un
 * fan qui n'a pas eu le sien (Kayla, 17-09-2026 : « un petit truc pour le
 * chatteur qui lui suggère de cliquer dessus »). Les bulles, elles, sont dans
 * le conseil à droite : les reproposer ici ferait deux fois la même chose à
 * l'écran.
 */
export async function laSuggestionDeScript(conv: ConversationDemo): Promise<SuggestionDeScript> {
  const enCours = leRunActif(conv.fiche.id) !== null;
  const flow = enCours ? null : await leFlowASuggerer(conv);
  const nouveau = conv.flowsRecus.length === 0;
  return {
    dossier: null,
    motif: null,
    bulles: [],
    flow:
      flow && nouveau
        ? { id: flow.id, nom: flow.name, motif: `il vient d'arriver et n'a pas encore eu « ${flow.name} »` }
        : null,
    raison: null,
    message_id: dernierEntrant(conv)?.id ?? null,
    videos: [],
  };
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Les boutons de Francis                                                     */
/* ────────────────────────────────────────────────────────────────────────── */

/** Les boutons sous Francis — les mêmes noms et libellés que la prod
 *  (`app/domain/services/francis/skills.py`). */
export const SKILLS_DE_FRANCIS = [
  { nom: "resume", libelle: "Résume-moi ce fan", vivant: true },
  { nom: "proposer", libelle: "Qu'est-ce que je lui propose ?", vivant: true },
  { nom: "donner_envie", libelle: "Comment lui donner envie d'acheter ?", vivant: true },
  { nom: "relance", libelle: "Écris-moi une relance", vivant: true },
  { nom: "confiance", libelle: "Comment le remettre en confiance ?", vivant: true },
  { nom: "renouer", libelle: "Comment renouer le lien ?", vivant: true },
];

/** Ce que Francis répond à un bouton, sur la situation de ce fan. */
export function laReponseDeFrancis(conv: ConversationDemo, skill: string): string {
  const l = lireLaSituation(conv);
  const etape = ETAPES_LISIBLES[l.etape] ?? l.etape;
  const phrase = l.messages[0];
  switch (skill) {
    case "resume":
      return `${resumeDe(conv)}\n\nOù il en est : ${etape}.\n${l.strategie}`;
    case "relance":
      return `Écris-lui ça, tel quel :\n\n« ${REVENIR} »\n\nUne phrase qui parle de lui, pas de prix ni de lien. S'il répond, tu reprends là où vous en étiez.`;
    case "confiance":
      return `Rassure-le sans te justifier : rappelle-lui que ce que tu lui proposes est fait pour LUI, et que tu tiens ta parole — tu livres dès qu'il a payé.\n\n${phrase ? `Tu peux lui écrire : « ${phrase} »` : ""}`.trim();
    case "renouer":
      return `Parle de lui avant de parler de toi : ce qu'il t'a raconté, ce qu'il aimait. Une question ouverte, puis tu le chauffes.\n\nPar exemple : « ${CHAUFFER} »`;
    case "proposer":
    case "donner_envie":
    default:
      return `${l.strategie}${phrase ? `\n\nÀ lui écrire : « ${phrase} »` : ""}${l.vente ? `\n\nPuis prépare la vente à ${l.vente} € — le bouton est dans le conseil, en haut.` : ""}`;
  }
}
